import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, GraduationCap, ChevronRight, CheckCircle2, Building2, Target, Award, BookOpen, TrendingUp, Users2, Shield } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/Button";
import { Helmet } from "react-helmet-async";

export function Home() {
  const ethicsBenefits = [
    "Atender requisitos legais e regulatórios",
    "Fortalecer programas de Compliance e ESG",
    "Prevenir fraudes, assédio e conflitos de interesse",
    "Demonstrar compromisso com a ética e a transparência",
    "Proteger colaboradores e a reputação da organização"
  ];

  const governanceServices = [
    "Estruturação de Programas de Governança Corporativa",
    "Desenvolvimento e revisão de políticas internas",
    "Mapeamento e gestão de riscos corporativos",
    "Implementação de controles internos",
    "Compliance regulatório e legal",
    "Código de Conduta e Ética",
    "Matriz de responsabilidades e alçadas",
    "Apoio à tomada de decisão e gestão estratégica"
  ];

  const trainingTopics = [
    "Ética e Conduta Corporativa",
    "Compliance e Programa de Integridade",
    "Assédio Moral e Assédio Sexual",
    "Diversidade, Respeito e Inclusão",
    "Canal de Ética e Proteção ao Denunciante",
    "Prevenção à Fraude e Corrupção",
    "Conflito de Interesses",
    "Proteção de Dados e LGPD",
    "Liderança Ética e Tom da Alta Direção",
    "Responsabilidade dos Gestores e Tomadores de Decisão"
  ];

  const trainingBenefits = [
    { icon: Target, title: "Fortalecimento da cultura organizacional", description: "Promove comportamentos alinhados aos valores e princípios da empresa." },
    { icon: TrendingUp, title: "Redução de riscos", description: "Diminui a ocorrência de desvios éticos, fraudes, assédio e não conformidades." },
    { icon: Shield, title: "Conformidade regulatória", description: "Auxilia no atendimento às exigências legais e às melhores práticas de governança e compliance." },
    { icon: Users2, title: "Maior engajamento dos colaboradores", description: "Profissionais bem orientados sentem-se mais seguros para agir corretamente e reportar situações inadequadas." },
    { icon: Award, title: "Desenvolvimento de lideranças", description: "Capacita gestores para atuarem como exemplos e multiplicadores da cultura de integridade." }
  ];

  return (
    <>
      <Helmet>
        <title>Portal Ethics - Liderando com Integridade</title>
        <meta name="description" content="Soluções completas em ética, compliance e governança corporativa para transformar sua cultura organizacional com transparência e segurança." />
      </Helmet>
      
      <div className="min-h-screen bg-background-light">
        <Header />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="relative h-[640px] flex items-center overflow-hidden">
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 to-transparent z-10" />
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80"
                alt="Corporate building"
                className="object-cover w-full h-full"
              />
            </div>
            
            <div className="relative z-20 max-w-7xl mx-auto px-6 w-full">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-blue/20 text-white rounded-full mb-6 border border-white/10 backdrop-blur-sm">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-xs font-bold tracking-wider uppercase">Compliance 360°</span>
                </div>
                
                <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
                  Liderando com <br />
                  <span className="italic text-blue-200">Integridade</span>
                </h1>
                
                <p className="text-lg text-white/80 mb-10 leading-relaxed font-light">
                  Soluções completas em ética, compliance e governança corporativa para transformar sua cultura organizacional com transparência e segurança.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <a href="http://wa.me/5511988890360" target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="group">
                      Falar com Especialista
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                  <Link to="/planos">
                    <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/20 bg-white/10 backdrop-blur-md">
                      Ver Planos
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Services Overview */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-4">
                <div className="max-w-xl">
                  <h2 className="text-primary-blue font-bold tracking-widest text-sm uppercase mb-3 flex items-center gap-2">
                    <span className="w-8 h-px bg-primary-blue"></span> Nossos Serviços
                  </h2>
                  <h3 className="text-4xl font-extrabold text-neutral-900 leading-tight">
                    Soluções integradas para excelência em conformidade.
                  </h3>
                </div>
                <p className="text-neutral-500 max-w-xs text-sm leading-relaxed mb-1">
                  Auxiliamos empresas a construir ambientes de trabalho éticos e resilientes através de tecnologia e consultoria especializada.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="group p-8 rounded-2xl bg-background-light border border-transparent hover:border-primary-blue/20 hover:bg-white hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-primary-blue/10 text-primary-blue rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-blue group-hover:text-white transition-colors">
                    <Users className="w-7 h-7" />
                  </div>
                  <h4 className="text-xl font-bold mb-4">Canais de Ética</h4>
                  <p className="text-neutral-600 leading-relaxed mb-6">
                    Sistemas seguros e anônimos para relatos e denúncias, garantindo a proteção do denunciante e a integridade da apuração.
                  </p>
                  <a href="#canal-etica" className="inline-flex items-center text-primary-blue font-bold text-sm gap-2 hover:gap-3 transition-all">
                    Saiba mais <ChevronRight className="w-5 h-5" />
                  </a>
                </div>

                <div className="group p-8 rounded-2xl bg-background-light border border-transparent hover:border-primary-blue/20 hover:bg-white hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-primary-blue/10 text-primary-blue rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-blue group-hover:text-white transition-colors">
                    <ShieldCheck className="w-7 h-7" />
                  </div>
                  <h4 className="text-xl font-bold mb-4">Governança</h4>
                  <p className="text-neutral-600 leading-relaxed mb-6">
                    Estruturas sólidas de políticas corporativas, //gestão de riscos e conformidade legal adaptadas ao seu modelo de negócio.
                  </p>
                  <a href="#governanca" className="inline-flex items-center text-primary-blue font-bold text-sm gap-2 hover:gap-3 transition-all">
                    Saiba mais <ChevronRight className="w-5 h-5" />
                  </a>
                </div>

                <div className="group p-8 rounded-2xl bg-background-light border border-transparent hover:border-primary-blue/20 hover:bg-white hover:shadow-xl transition-all">
                  <div className="w-14 h-14 bg-primary-blue/10 text-primary-blue rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary-blue group-hover:text-white transition-colors">
                    <GraduationCap className="w-7 h-7" />
                  </div>
                  <h4 className="text-xl font-bold mb-4">Treinamentos</h4>
                  <p className="text-neutral-600 leading-relaxed mb-6">
                    Capacitação contínua em ética e integridade para colaboradores e lideranças, promovendo uma cultura de consciência.
                  </p>
                  <a href="#treinamentos" className="inline-flex items-center text-primary-blue font-bold text-sm gap-2 hover:gap-3 transition-all">
                    Saiba mais <ChevronRight className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Canal de Ética Section */}
          <section id="canal-etica" className="py-24 bg-background-light">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-blue/10 text-primary-blue rounded-full mb-6">
                    <Users className="w-4 h-4" />
                    <span className="text-xs font-bold tracking-wider uppercase">Canal de Ética</span>
                  </div>
                  <h2 className="text-4xl font-black text-neutral-900 leading-tight mb-6">
                    Canal de Ética em Conformidade com a Legislação Brasileira
                  </h2>
                  <p className="text-neutral-600 leading-relaxed mb-6">
                    A implementação de um Canal de Ética tornou-se um importante requisito de governança corporativa, integridade e conformidade regulatória.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-6">
                    A <strong>Lei nº 12.846/2013 (Lei Anticorrupção)</strong> e o <strong>Decreto nº 11.129/2022</strong>, que regulamenta os Programas de Integridade, destacam a importância de mecanismos de denúncia, proteção ao denunciante e procedimentos para apuração de irregularidades como elementos fundamentais de um programa de compliance eficaz.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-6">
                    Além disso, a <strong>Lei nº 14.457/2022</strong>, que instituiu o Programa Emprega + Mulheres, passou a exigir que empresas com Comissão Interna de Prevenção de Acidentes e de Assédio (CIPA) adotem medidas de prevenção e combate ao assédio sexual e outras formas de violência no ambiente de trabalho, incluindo canais para recebimento e acompanhamento de denúncias.
                  </p>
                  <p className="text-neutral-600 leading-relaxed">
                    Para empresas que atuam em setores regulados ou que mantêm contratos com o poder público, a existência de um Canal de Ética também é frequentemente considerada um requisito de boas práticas de governança e integridade.
                  </p>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-blue/20 to-primary-blue/5 rounded-3xl transform rotate-3"></div>
                  <div className="relative bg-white rounded-3xl p-8 shadow-xl">
                    <h3 className="text-2xl font-bold text-neutral-900 mb-6">Sua empresa está preparada?</h3>
                    <p className="text-neutral-600 mb-6">Um Canal de Ética estruturado ajuda:</p>
                    <div className="space-y-4">
                      {ethicsBenefits.map((benefit, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary-blue flex-shrink-0 mt-0.5" />
                          <span className="text-neutral-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Governança Section */}
          <section id="governanca" className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-blue/10 text-primary-blue rounded-full mb-6">
                  <Building2 className="w-4 h-4" />
                  <span className="text-xs font-bold tracking-wider uppercase">Governança Corporativa</span>
                </div>
                <h2 className="text-4xl font-black text-neutral-900 leading-tight mb-6">
                  Estrutura, Controle e Sustentabilidade para o seu Negócio
                </h2>
                <p className="text-neutral-600 leading-relaxed">
                  Uma governança corporativa eficiente é a base para empresas que desejam crescer de forma sustentável, reduzir riscos e fortalecer a confiança de clientes, investidores, parceiros e colaboradores.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div className="order-2 lg:order-1">
                  <div className="bg-background-light rounded-3xl p-8">
                    <h3 className="text-2xl font-bold text-neutral-900 mb-6">O que é Governança Corporativa?</h3>
                    <p className="text-neutral-600 leading-relaxed mb-6">
                      Governança corporativa é o conjunto de práticas, políticas e estruturas que orientam a gestão da empresa, promovendo transparência, responsabilidade, equidade e conformidade com as leis e regulamentos aplicáveis.
                    </p>
                    <p className="text-neutral-600 leading-relaxed">
                      Mais do que uma exigência de mercado, a governança é uma ferramenta estratégica para proteger a organização, aumentar sua competitividade e gerar valor a longo prazo.
                    </p>
                  </div>
                </div>
                <div className="order-1 lg:order-2">
                  <img
                    src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
                    alt="Governança Corporativa"
                    className="rounded-3xl shadow-xl w-full h-96 object-cover"
                  />
                </div>
              </div>

              <div className="bg-primary-blue rounded-3xl p-8 md:p-12">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-black text-white mb-4">
                    Como a Ethics Compliance pode ajudar?
                  </h3>
                  <p className="text-white/80 max-w-2xl mx-auto">
                    Desenvolvemos modelos de governança adaptados à realidade e ao porte da sua empresa, considerando suas necessidades específicas e os riscos inerentes ao seu segmento de atuação.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {governanceServices.map((service, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <CheckCircle2 className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                      <span className="text-white text-sm">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Treinamentos Section */}
          <section id="treinamentos" className="py-24 bg-background-light">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-blue/10 text-primary-blue rounded-full mb-6">
                  <GraduationCap className="w-4 h-4" />
                  <span className="text-xs font-bold tracking-wider uppercase">Treinamentos</span>
                </div>
                <h2 className="text-4xl font-black text-neutral-900 leading-tight mb-6">
                  Desenvolva uma cultura de ética que gera resultados
                </h2>
                <p className="text-neutral-600 leading-relaxed">
                  A construção de uma cultura organizacional ética não acontece apenas por meio de políticas e normas. Ela depende do conhecimento, da conscientização e do engajamento das pessoas.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <img
                    src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80"
                    alt="Treinamentos"
                    className="rounded-3xl shadow-xl w-full h-96 object-cover"
                  />
                </div>
                <div>
                  <div className="bg-white rounded-3xl p-8 shadow-lg">
                    <h3 className="text-2xl font-bold text-neutral-900 mb-6">Capacitação que transforma comportamento</h3>
                    <p className="text-neutral-600 leading-relaxed mb-6">
                      Na Ethics Compliance, desenvolvemos treinamentos personalizados para fortalecer a cultura ética da sua empresa, promovendo o entendimento prático das regras, responsabilidades e expectativas relacionadas ao compliance e à integridade corporativa.
                    </p>
                    <p className="text-neutral-600 leading-relaxed">
                      Nossos programas são adaptados ao perfil, segmento e realidade de cada organização, tornando o aprendizado mais relevante e eficaz.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
                <div>
                  <h3 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center gap-3">
                    <BookOpen className="w-8 h-8 text-primary-blue" />
                    Temas que podem ser abordados
                  </h3>
                  <div className="bg-white rounded-3xl p-8 shadow-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {trainingTopics.map((topic, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-primary-blue flex-shrink-0 mt-0.5" />
                          <span className="text-neutral-700">{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center gap-3">
                    <Award className="w-8 h-8 text-primary-blue" />
                    Benefícios dos Treinamentos
                  </h3>
                  <div className="space-y-4">
                    {trainingBenefits.map((benefit, index) => {
                      const Icon = benefit.icon;
                      return (
                        <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-primary-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Icon className="w-6 h-6 text-primary-blue" />
                            </div>
                            <div>
                              <h4 className="font-bold text-neutral-900 mb-2">{benefit.title}</h4>
                              <p className="text-neutral-600 text-sm">{benefit.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          

          {/* CTA Section */}
          <section className="py-20 bg-primary-blue relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>
            <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
                Pronto para elevar o nível de conformidade da sua empresa?
              </h2>
            
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="http://wa.me/5511988890360" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-white text-primary-blue hover:bg-neutral-100">
                    Solicitar Demonstração
                  </Button>
                </a>
                
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}